import { type ButtonHTMLAttributes, forwardRef } from 'react';

/**
 * Button primitive aligned to mockup-1 .fbtn standard button system.
 * Sprint 31.7: Updated to use CSS classes from index.css (no Tailwind).
 * Sprint 10.2: Added aria-busy for loading state, sr-only loading announcement.
 *
 * Contract:
 * - Variants map to CSS classes: .fbtn.primary / .fbtn.secondary / .fbtn.danger / .fbtn (ghost)
 * - Sizes: sm → .fbtn.small, md → default
 * - Use this instead of ad-hoc <button className="..."> to avoid visual drift.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a subtle text swap to indicate loading; caller should also disable external actions. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, disabled, className, children, ...rest },
  ref
) {
  const isDisabled = disabled || loading;

  // Build class list using CSS classes from index.css
  const classes = ['fbtn'];
  
  // Variant classes
  if (variant === 'primary') classes.push('primary');
  else if (variant === 'secondary') classes.push('secondary');
  else if (variant === 'danger') classes.push('danger');
  // ghost uses base .fbtn styling - no additional class needed
  
  // Size class
  if (size === 'sm') classes.push('small');
  
  // Add any custom className
  if (className) classes.push(className);

  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-busy={loading || undefined}
      className={classes.join(' ')}
      {...rest}
    >
      {/* BL-9: was `btn-loading`, which has no rule anywhere -- so the design system's
          OWN button emitted a dead class, and the loading state rendered as bare "…"
          text. Renamed into the `fbtn` family it belongs to, and given a real rule in
          index.css. A component that ships the defect it exists to prevent is the worst
          place for one. */}
      {loading ? <span className="fbtn-loading" aria-hidden="true">…</span> : children}
      {loading && <span className="sr-only">Loading</span>}
    </button>
  );
});
